import { Link } from "react-router-dom";
import { Instagram, Headphones, Mail, Check, ArrowRight } from "lucide-react";
import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const SUPPORT_EMAIL = "pip@pipbennett.com";

function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      await api.post("/newsletter", { name, email });
      setDone(true);
      toast.success("Welcome — see you Sunday.");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't sign you up. Try again?";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className="bg-cream/10 border border-cream/20 rounded-2xl p-5 flex items-center gap-3"
        data-testid="footer-newsletter-success"
      >
        <span className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-gold text-ink flex-shrink-0">
          <Check size={16} strokeWidth={1.8} />
        </span>
        <div className="text-sm text-cream/85">You're in. Look out for me on Sunday morning.</div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="footer-newsletter-form">
      <input
        required
        placeholder="First name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full bg-cream/10 border border-cream/20 rounded-full px-5 py-3 text-cream placeholder-cream/40 focus:outline-none focus:border-gold transition text-sm"
        data-testid="footer-newsletter-name"
      />
      <input
        required
        type="email"
        placeholder="you@yours.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full bg-cream/10 border border-cream/20 rounded-full px-5 py-3 text-cream placeholder-cream/40 focus:outline-none focus:border-gold transition text-sm"
        data-testid="footer-newsletter-email"
      />
      <button
        type="submit"
        disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-ink rounded-full px-5 py-3 font-medium text-sm transition-all disabled:opacity-50"
        data-testid="footer-newsletter-submit"
      >
        {loading ? "Saving…" : "Subscribe"}
        {!loading && <ArrowRight size={14} strokeWidth={1.8} />}
      </button>
      <p className="text-xs text-cream/50 leading-relaxed">
        One quiet email each Sunday. Unsubscribe anytime — no hard feelings.
      </p>
      {error && (
        <p className="text-xs text-gold leading-relaxed" role="alert" data-testid="footer-newsletter-error">
          {error}
        </p>
      )}
    </form>
  );
}

export default function Footer() {
  return (
    <footer className="bg-sage text-cream mt-20" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-20 lg:py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="font-serif-display text-3xl md:text-4xl leading-tight">
            Pip <span className="text-gold italic">Bennett</span>
          </div>
          <p className="mt-6 text-cream/75 leading-relaxed max-w-md">
            Strategic life coaching &amp; clinical hypnotherapy for the person done second-guessing themselves.
            Decisiveness, self-trust, and the quiet power of knowing yourself.
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="mt-8 inline-flex items-center gap-2 text-cream/85 hover:text-gold transition text-sm"
            data-testid="footer-support-email"
          >
            <Mail size={14} strokeWidth={1.5} />
            {SUPPORT_EMAIL}
          </a>
          <div className="mt-6 flex items-center gap-4">
            <a
              href="https://www.instagram.com/pausewithpip/"
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-cream/25 hover:bg-cream hover:text-sage transition-all"
              data-testid="footer-instagram"
              aria-label="Instagram"
            >
              <Instagram size={16} strokeWidth={1.5} />
            </a>
            <a
              href="https://open.spotify.com/show/0VaUEuK7KOrBv19THWGM2v"
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-cream/25 hover:bg-cream hover:text-sage transition-all"
              data-testid="footer-podcast"
              aria-label="Pip's Magnificent Midlife Podcast"
            >
              <Headphones size={16} strokeWidth={1.5} />
            </a>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="eyebrow text-cream/60 mb-5">Explore</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/" className="hover:text-gold transition">Home</Link></li>
            <li><Link to="/about" className="hover:text-gold transition">About</Link></li>
            <li><Link to="/the-menopause-invitation" className="hover:text-gold transition">Menopause Invitation</Link></li>
            <li><Link to="/a-mans-guide-to-her-menopause" className="hover:text-gold transition" data-testid="footer-mens-guide-link">A Man's Guide to Her Menopause</Link></li>
            <li><Link to="/the-rewired-life" className="hover:text-gold transition">The Rewired Life</Link></li>
            <li><Link to="/podcast" className="hover:text-gold transition">Podcast</Link></li>
            <li><Link to="/faq" className="hover:text-gold transition" data-testid="footer-faq-link">FAQ</Link></li>
            <li><Link to="/free-resources" className="hover:text-gold transition" data-testid="footer-free-resources-link">Free Resources</Link></li>
            <li><Link to="/starter-kit" className="hover:text-gold transition">Free Starter Kit</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="eyebrow text-cream/60 mb-5">Connect</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/contact" className="hover:text-gold transition">Contact</Link></li>
            <li><Link to="/waitlist" className="hover:text-gold transition">1:1 Waitlist</Link></li>
            <li><Link to="/login" className="hover:text-gold transition">Member Login</Link></li>
            <li>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-gold transition" data-testid="footer-support-link">
                Support &amp; Refunds
              </a>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="eyebrow text-cream/60 mb-3">Sunday with Pip</div>
          <p className="text-sm text-cream/70 mb-5 leading-relaxed">
            One quiet thought, each Sunday morning. A small invitation. Nothing more.
          </p>
          <NewsletterSignup />
        </div>
      </div>

      <div className="border-t border-cream/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-cream/50">
          <div>© {new Date().getFullYear()} Pip Bennett. All rights reserved.</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 items-center">
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Privacy%20question`} className="hover:text-gold">Privacy</a>
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Terms%20question`} className="hover:text-gold">Terms</a>
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Refund%20request`} className="hover:text-gold" data-testid="footer-refund-link">Refunds</a>
            <a
              href="https://app.emergent.sh/?utm_source=emergent-badge"
              target="_blank"
              rel="noopener noreferrer"
              className="text-cream/35 hover:text-gold/70 transition-colors"
              data-testid="footer-emergent-credit"
            >
              Made with Emergent
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
